import { InfiniteScrollSentinel } from "@circle-react-shared/InfiniteScrollSentinel";
import { Drawer } from "@circleco/compass/components/Drawer";
import { Modal } from "@circleco/compass/components/Modal";
import { ScrollArea } from "@circleco/compass/components/ScrollArea";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { Fragment, useState } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";

import { MemberRowSkeleton } from "./MemberRowSkeleton";
import { MemberRowsSkeleton } from "./MemberRowsSkeleton";
import { ReactionDrawerHeader } from "./ReactionDrawerHeader";
import { ReactionFilterTabs } from "./ReactionFilterTabs";

interface ReactionLikesDialogProps<TMember extends { id: number | string }> {
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isInitialLoading: boolean;
  members: TMember[];
  onOpenChange: (open: boolean) => void;
  onOpenChangeComplete: (open: boolean) => void;
  open: boolean;
  /** Omit to hide the reaction filter tabs. */
  reactionFilter?: ComponentPropsWithoutRef<typeof ReactionFilterTabs>;
  renderMember: (member: TMember, className: string | undefined) => ReactNode;
  skeletonCount?: number | null;
  title: string;
}

export const ReactionLikesDialog = <TMember extends { id: number | string }>({
  fetchNextPage,
  hasNextPage,
  isInitialLoading,
  members,
  onOpenChange,
  onOpenChangeComplete,
  open,
  reactionFilter,
  renderMember,
  skeletonCount,
  title,
}: ReactionLikesDialogProps<TMember>) => {
  const isMobile = useIsMobile();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const rowClassName = isMobile ? "px-4 py-3" : undefined;

  const rows = (
    <div
      className={classNames(
        "flex flex-col",
        isMobile ? "divide-primary -mx-4 divide-y" : "gap-4",
      )}
    >
      {members.map(member => (
        <Fragment key={member.id}>
          {renderMember(member, rowClassName)}
        </Fragment>
      ))}
      {scrollRoot && (
        <InfiniteScrollSentinel
          dataLength={members.length}
          hasMore={hasNextPage}
          onLoadMore={fetchNextPage}
          scrollContainer={scrollRoot}
          loader={<MemberRowSkeleton className={rowClassName} />}
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer.Root
        open={open}
        onOpenChange={onOpenChange}
        onOpenChangeComplete={onOpenChangeComplete}
        snapTo="half"
      >
        <Drawer.Content ref={setScrollRoot} aria-label={title}>
          <ReactionDrawerHeader title={title} />
          {reactionFilter && (
            <ReactionFilterTabs className="px-0 py-2" {...reactionFilter} />
          )}
          {isInitialLoading ? (
            <MemberRowsSkeleton
              className="divide-primary -mx-4 divide-y"
              count={skeletonCount}
              rowClassName="px-4 py-3"
            />
          ) : (
            rows
          )}
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  return (
    <Modal.Root
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      size="lg"
    >
      <Modal.Content>
        <Modal.Header title={title} showCloseButton />
        {reactionFilter && <ReactionFilterTabs {...reactionFilter} />}
        <Modal.Body customLayout>
          <ScrollArea
            viewportRef={setScrollRoot}
            scrollFades={false}
            className={reactionFilter ? "h-96" : "max-h-96"}
          >
            {isInitialLoading ? (
              <MemberRowsSkeleton count={skeletonCount} />
            ) : (
              rows
            )}
          </ScrollArea>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
